import {
    Button,
    DatePickerProvider,
    DatePickerWeekdays,
    Flexer,
    Heading,
    IconLib,
    Input,
    InputControl,
    InputPopover,
    InputSuffix,
    Label,
    Link,
    Popover,
    ScrollingDatePicker,
    TimePicker,
    Toggle,
    View,
    useCheck,
    useVisibility,
} from '@fold-dev/core'
import { CalendarTypes } from '../calendar'
import React, { useMemo, useRef, useState } from 'react'
import { getDateSelectTimeFormat } from '../../helpers'
import { Repeat, defaultWeekdays } from './repeat'
import { getRepeatFrequencyText } from '../../helpers/dates'
import { weekdays } from '../rich-input'

export type DateSelectProps = {
    wholeDay?: boolean
    dateRange?: boolean
    start?: Date
    end?: Date
    repeat?: CalendarTypes.Repeat
    presets?: { preset: string; label: string }[]
    monthNames?: string[]
    onSave?: (val) => void
    onCancel?: () => void
}

export const DateSelect = (props: DateSelectProps) => {
    const {
        presets = [
            { preset: 'today', label: 'Today' },
            { preset: 'tomorrow', label: 'Tomorrow' },
            { preset: 'next-week', label: 'Next week' },
            { preset: 'next-month', label: 'Next month' },
            { preset: 'next-year', label: 'Next year' },
            { preset: 'this-week', label: 'This week' },
            { preset: 'this-month', label: 'This month' },
            { preset: 'this-weekend', label: 'This Weekend' },
            { preset: 'end-of-week', label: 'End of This Week' },
            { preset: 'end-of-month', label: 'End of This Month' },
            { preset: 'in-two-weeks', label: 'In Two Weeks' },
        ],
        monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        onSave,
        onCancel,
    } = props
    const ref = useRef(null)
    const { visible, show, hide } = useVisibility(false)
    const dateRange = useCheck(!!props.end)
    const wholeDay = useCheck(!!props.wholeDay)
    const [start, setStart] = useState(props.start)
    const [end, setEnd] = useState(props.end)
    const [repeat, setRepeat] = useState(props.repeat)
    const [selection, setSelection] = useState<any[]>(
        !!start && !!end ? [[start, end]] : !!start ? [[start, start]] : []
    )
    const [defaultDate, setDefaultDate] = useState(start || end || new Date())
    const repeatLabel = useMemo(
        () => (repeat ? getRepeatFrequencyText(repeat, defaultWeekdays) : 'Add repeat schedule'),
        [repeat]
    )

    const selectPresetDate = (preset) => {
        const now = new Date()
        let start = new Date()
        let end = new Date()
        const currentDate = new Date()
        const dayOfWeek = currentDate.getDay()

        switch (preset) {
            case 'today':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)

                break

            case 'tomorrow':
                start.setDate(now.getDate() + 1)
                end.setDate(now.getDate() + 1)
                end.setHours(23, 59, 59, 999)
                break

            case 'next-week':
                const daysUntilNextMonday = (8 - dayOfWeek) % 7 || 7
                start.setDate(currentDate.getDate() + daysUntilNextMonday)
                start.setHours(0, 0, 0, 0)
                end = start
                break

            case 'next-month':
                const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
                start = nextMonth
                end = nextMonth
                break

            case 'next-year':
                start = new Date(now.getFullYear() + 1, 0, 1)
                end = start
                break

            case 'this-week':
                const startOffset = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek
                start.setDate(now.getDate() + startOffset)
                start.setHours(0, 0, 0, 0)
                end = start
                break

            case 'this-month':
                start = new Date(now.getFullYear(), now.getMonth(), 1)
                end = start
                break

            case 'this-weekend':
                const saturday = new Date(now)
                saturday.setDate(now.getDate() + (6 - dayOfWeek))
                saturday.setHours(0, 0, 0, 0)

                const sunday = new Date(saturday)
                sunday.setDate(saturday.getDate() + 1)
                sunday.setHours(23, 59, 59, 999)

                start = saturday
                end = sunday
                break

            case 'end-of-week':
                const sundayEnd = new Date(now)
                sundayEnd.setDate(now.getDate() + (7 - dayOfWeek))
                sundayEnd.setHours(23, 59, 59, 999)
                start = sundayEnd
                end = sundayEnd
                break

            case 'end-of-month':
                const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                lastDayOfMonth.setHours(23, 59, 59, 999)
                start = lastDayOfMonth
                end = lastDayOfMonth
                break

            case 'in-two-weeks':
                start.setDate(now.getDate() + 14)
                start.setHours(0, 0, 0, 0)
                end.setDate(now.getDate() + 14)
                end.setHours(23, 59, 59, 999)
                break
        }

        setSelection([[start, end]])
        setDefaultDate(start)
        setStart(start)
        setEnd(end)
        wholeDay.setChecked(true)
    }

    const handleSelection = (date: Date) => {
        if (dateRange.checked) {
            if (selection.length == 0) {
                setSelection([[date, null]])
            } else {
                const selected = selection[0]

                if (!selected[0]) {
                    setSelection([date, null])
                } else if (!!selected[0] && !!selected[1]) {
                    setSelection([[date, null]])
                } else if (!!selected[0] && !selected[1]) {
                    if (selected[0] > date) {
                        setSelection([[date, selected[0]]])
                        setStart(date)
                        setEnd(selected[0])
                    } else {
                        setSelection([[selected[0], date]])
                        setStart(selected[0])
                        setEnd(date)
                    }
                }
            }
        } else {
            setSelection([[date, date]])
            setStart(date)
            setEnd(date)
        }
    }

    const handleStartDate = (date) => {
        setStart(date)
        setSelection([[date, selection[0] ? selection[0][1] : new Date()]])
    }

    const handleEndDate = (date) => {
        setEnd(date)
        setSelection([[selection[0] ? selection[0][0] : new Date(), date]])
    }

    const handleClear = () => {
        setRepeat(undefined)
        setStart(undefined)
        setEnd(undefined)
        setSelection([])
        onSave({
            start: undefined,
            end: undefined,
            repeat: null,
            wholeDay: false,
        })
    }

    const handleSave = () => {
        if (dateRange.checked || !wholeDay.checked) {
            onSave({
                start,
                end,
                repeat: null,
                wholeDay: wholeDay.checked,
            })
        } else {
            onSave({
                start,
                end: null,
                repeat,
                wholeDay: wholeDay.checked,
            })
        }
    }

    const handleDateRangeCheck = () => {
        const startDate = new Date(start)
        setEnd(startDate)
        setSelection([[startDate, startDate]])
        dateRange.check()
    }

    return (
        <div className="f-date-select">
            <div className="f-date-select__date f-row">
                <div className="f-date-select__presets f-col">
                    {presets.map(({ preset, label }, index) => (
                        <Link
                            key={index}
                            size="sm"
                            className="f-underline"
                            onClick={() => selectPresetDate(preset)}>
                            {label}
                        </Link>
                    ))}
                </div>

                <div className="f-date-select__date-picker">
                    <DatePickerProvider>
                        {/*  <DatePickerWeekdays
                            size="xs"
                            weekdays={weekdays}
                            height={30}
                            style={{
                                '--f-month-weekday-padding': '1rem 0 0 0',
                                '--f-color-text': 'var(--f-color-text-weakest)',
                            }}
                        /> */}
                        <ScrollingDatePicker
                            height={300}
                            ref={ref}
                            defaultDate={defaultDate}
                            selection={selection}
                            onChange={handleSelection}
                            monthTitle={(date: Date) => (
                                <View
                                    column
                                    colorToken="accent"
                                    as="span"
                                    width="100%">
                                    <span
                                        className="f-text sm f-date-select__month"
                                        style={{ padding: '0.5rem 0.9rem' }}>
                                        {monthNames[date.getMonth()]} {date.getFullYear()}
                                    </span>
                                    <span
                                        className="f-row"
                                        style={{ width: '100%', paddingBottom: 4 }}>
                                        {weekdays.map((weekday, index) => (
                                            <span
                                                className="f-text sm f-date-select__weekday"
                                                key={index}>
                                                {weekday.slice(0, 2)}
                                            </span>
                                        ))}
                                    </span>
                                </View>
                            )}
                        />
                    </DatePickerProvider>
                </div>

                <div className="f-date-select__time f-col">
                    <Heading
                        as="h6"
                        fontWeight={500}
                        colorToken="accent">
                        {dateRange.checked || !wholeDay.checked ? 'Start time:' : 'Due time:'}
                    </Heading>
                    <InputPopover
                        content={
                            <View p={20}>
                                <TimePicker
                                    autoFocus
                                    showAmPm={false}
                                    minutesDivider={1}
                                    showSeconds={false}
                                    height={200}
                                    date={start}
                                    onChange={handleStartDate}
                                />
                            </View>
                        }>
                        <InputControl width={150}>
                            <Input
                                disabled={wholeDay.checked}
                                style={{ cursor: 'pointer' }}
                                onChange={(e) => null}
                                placeholder=""
                                value={wholeDay.checked ? '--' : getDateSelectTimeFormat(start)}
                            />
                            <InputSuffix>
                                <IconLib
                                    icon="time"
                                    style={{ pointerEvents: 'none' }}
                                />
                            </InputSuffix>
                        </InputControl>
                    </InputPopover>

                    {(dateRange.checked || !wholeDay.checked) && (
                        <>
                            <Heading
                                as="h6"
                                fontWeight={500}
                                colorToken="accent">
                                End time:
                            </Heading>
                            <InputPopover
                                content={
                                    <View p={20}>
                                        <TimePicker
                                            autoFocus
                                            showAmPm={false}
                                            minutesDivider={1}
                                            showSeconds={false}
                                            height={200}
                                            date={end}
                                            onChange={handleEndDate}
                                        />
                                    </View>
                                }>
                                <InputControl width={150}>
                                    <Input
                                        disabled={wholeDay.checked}
                                        style={{ cursor: 'pointer' }}
                                        onChange={(e) => null}
                                        placeholder=""
                                        value={wholeDay.checked ? '--' : getDateSelectTimeFormat(end)}
                                    />
                                    <InputSuffix>
                                        <IconLib
                                            icon="time"
                                            style={{ pointerEvents: 'none' }}
                                        />
                                    </InputSuffix>
                                </InputControl>
                            </InputPopover>
                        </>
                    )}

                    <View
                        row
                        gap={10}>
                        <Toggle
                            id="f-date-select-time"
                            onChange={wholeDay.check}
                            on={wholeDay.checked}
                        />
                        <Label htmlFor="f-date-select-time">Whole day</Label>
                    </View>
                    <Flexer />
                    <Popover
                        arrow
                        focusTrap
                        width="fit-content"
                        anchor="top-right"
                        content={
                            <Repeat
                                border="none"
                                repeat={repeat}
                                onSave={(repeat) => {
                                    hide()
                                    setRepeat(repeat)
                                }}
                                onDismiss={hide}
                                onChange={(repeat) => setRepeat(repeat)}
                                onClear={() => {
                                    setRepeat(null)
                                    hide()
                                }}
                            />
                        }
                        isVisible={visible}
                        onDismiss={hide}>
                        <div className="f-row">
                            {!dateRange.checked && (
                                <Link
                                    style={{ maxWidth: 150 }}
                                    size="sm"
                                    colorToken="text-weaker"
                                    className="f-underline"
                                    onClick={show}>
                                    {repeatLabel}
                                </Link>
                            )}
                        </div>
                    </Popover>
                </div>
            </div>

            <div className="f-date-select__footer f-row">
                <Toggle
                    id="f-date-select-range"
                    onChange={handleDateRangeCheck}
                    on={dateRange.checked}
                />
                <Label htmlFor="f-date-select-range">Date range</Label>
                <div style={{ flex: 1 }} />
                <Button
                    subtle
                    onClick={handleClear}>
                    Clear
                </Button>
                <Button onClick={onCancel}>Cancel</Button>
                <Button
                    variant="accent"
                    onClick={handleSave}>
                    Okay
                </Button>
            </div>
        </div>
    )
}
